import { useEffect, useState } from "react";
import api, { API } from "@/lib/api";
import { toast } from "sonner";
import { ClipboardCheck, ChevronRight, Loader2, X, Download } from "lucide-react";
import LiveSearch, { matchesQuery } from "@/components/LiveSearch";

export default function AdminPjc() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [query, setQuery] = useState("");
  const [detail, setDetail] = useState(null);
  const [fromD, setFromD] = useState("");
  const [toD, setToD] = useState("");

  const downloadPdf = async (pjc) => {
    try {
      const res = await fetch(`${API}/pjc/${pjc.id}/pdf`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("etel_token")}` },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      const safe = (pjc.obra || "PJC").replace(/[^\w]+/g, "_").slice(0, 40);
      a.href = url;
      a.download = `PJC_${safe}_${pjc.fecha || ""}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 2000);
    } catch (e) {
      toast.error("No se pudo descargar el PDF");
    }
  };

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/pjc/personal", {
        params: {
          fecha_desde: fromD || undefined,
          fecha_hasta: toD || undefined,
          limit: 500,
        },
      });
      setItems(data || []);
    } catch (e) { toast.error(e?.response?.data?.detail || "No se pudo cargar"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const visible = items.filter((p) =>
    matchesQuery(query, p.user_name, p.obra, p.fecha, p.notas)
  );

  return (
    <div className="space-y-4" data-testid="admin-pjc-page">
      <header>
        <div className="label-up text-[#F5A623]">Auditoría</div>
        <h1 className="font-display font-black text-3xl tracking-tight mt-1">Pre-Job Checks del equipo</h1>
        <p className="text-zinc-600 text-sm mt-1">Todos los PJC firmados por operarios (registro central).</p>
      </header>

      <LiveSearch value={query} onChange={setQuery}
        placeholder="Buscar por operario, obra, fecha…" testId="admin-pjc-search" />

      <div className="brutal-card p-3 grid grid-cols-2 gap-2 items-end">
        <div>
          <label className="label-up block mb-1">Desde</label>
          <input type="date" className="brutal-input w-full" data-testid="admin-pjc-from"
            value={fromD} onChange={(e) => setFromD(e.target.value)} />
        </div>
        <div className="flex gap-2 items-end">
          <div className="flex-1">
            <label className="label-up block mb-1">Hasta</label>
            <input type="date" className="brutal-input w-full" data-testid="admin-pjc-to"
              value={toD} onChange={(e) => setToD(e.target.value)} />
          </div>
          <button type="button" onClick={load} data-testid="admin-pjc-apply"
            className="brutal-btn-dark h-11 px-3 text-xs">Aplicar</button>
        </div>
      </div>

      {loading && (
        <div className="brutal-card p-6 text-center text-slate-500 inline-flex items-center gap-2 justify-center w-full">
          <Loader2 size={16} className="animate-spin" /> Cargando…
        </div>
      )}

      {!loading && visible.length === 0 && (
        <div className="brutal-card p-6 text-center text-slate-500">Sin resultados.</div>
      )}

      <ul className="space-y-2">
        {visible.map((p) => (
          <li key={p.id}>
            <button type="button" onClick={() => setDetail(p)}
              data-testid={`admin-pjc-item-${p.id}`}
              className="w-full brutal-card p-3 flex items-center gap-3 text-left hover:-translate-y-[1px] transition-transform">
              <div className="w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center shrink-0">
                <ClipboardCheck size={18} className="text-slate-500" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-sm truncate">{p.obra || "Sin obra"}</div>
                <div className="text-[11px] text-slate-500 truncate">
                  {p.fecha} · {p.user_name} · {(p.firmantes_extra?.length || 0) + 1} firma{p.firmantes_extra?.length ? "s" : ""}
                </div>
              </div>
              <ChevronRight size={16} className="text-slate-400 shrink-0" />
            </button>
          </li>
        ))}
      </ul>

      {detail && (
        <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4"
          onClick={() => setDetail(null)} data-testid="admin-pjc-detail">
          <div className="w-full sm:max-w-lg bg-white rounded-t-2xl sm:rounded-2xl shadow-2xl max-h-[92vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}>
            <div className="sticky top-0 bg-white border-b border-slate-200 px-5 py-3 flex items-center justify-between">
              <div>
                <div className="label-up text-[#F5A623]">PJC</div>
                <h3 className="font-display font-black text-lg text-[#0B2A4A] leading-tight">{detail.obra}</h3>
                <div className="text-[11px] text-slate-500">{detail.fecha} · {detail.user_name}</div>
              </div>
              <button type="button" onClick={() => setDetail(null)} className="text-slate-500 hover:text-rose-600"><X size={20} /></button>
            </div>
            <div className="p-5 space-y-4">
              <button type="button" onClick={() => downloadPdf(detail)}
                data-testid="admin-pjc-download-pdf"
                className="w-full h-10 rounded-lg bg-[#0E78C2] text-white text-sm font-semibold inline-flex items-center justify-center gap-2 hover:bg-[#0a5b93]">
                <Download size={14} /> Descargar PDF
              </button>
              <div>
                <div className="label-up mb-1">Respuestas</div>
                <ul className="text-xs space-y-1">
                  {Object.entries(detail.answers || {}).map(([k, v]) => (
                    <li key={k} className="flex items-center gap-2">
                      <span className={`inline-flex items-center justify-center w-6 h-5 rounded text-[10px] font-bold ${
                        v === "si" ? "bg-emerald-600 text-white" : "bg-rose-600 text-white"
                      }`}>{v === "si" ? "SÍ" : "NO"}</span>
                      <span className="text-slate-700 truncate">{k}</span>
                    </li>
                  ))}
                </ul>
              </div>
              {detail.notas && (
                <div>
                  <div className="label-up mb-1">Notas</div>
                  <p className="text-sm whitespace-pre-wrap text-slate-700">{detail.notas}</p>
                </div>
              )}
              <div>
                <div className="label-up mb-2">Firmas ({(detail.firmantes_extra?.length || 0) + 1})</div>
                <div className="space-y-2">
                  <div className="flex items-center gap-3 border border-slate-200 rounded-lg p-2">
                    <div className="flex-1 min-w-0"><div className="text-sm font-semibold text-[#0B2A4A] truncate">{detail.user_name}</div></div>
                    {detail.signature && <img src={detail.signature} alt="firma" className="h-12 w-24 object-contain bg-slate-50 border border-slate-200 rounded" />}
                  </div>
                  {(detail.firmantes_extra || []).map((f, i) => (
                    <div key={i} className="flex items-center gap-3 border border-slate-200 rounded-lg p-2">
                      <div className="flex-1 min-w-0"><div className="text-sm font-semibold text-[#0B2A4A] truncate">{f.name}</div></div>
                      <img src={f.signature} alt="firma" className="h-12 w-24 object-contain bg-slate-50 border border-slate-200 rounded" />
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
